import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert, Badge, Button, Form, Modal, Nav, Spinner, Table, Tab,
} from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faRotate, faGavel, faPaperPlane, faMoneyBillTransfer,
  faHandshake, faFileUpload, faArrowUp, faClock,
} from "@fortawesome/free-solid-svg-icons";
import { toast } from "react-toastify";
import Utils from "../../configs/Utils";
import Enums from "../../configs/Enums";

/**
 * Detail + action modal for a single PayPal dispute.
 *
 * Loads the full document (incl. `raw`) from the server when opened, so we
 * can render messages, evidences, transactions and the timeline.
 *
 * Actions invoke the per-action endpoints. After each action the modal
 * receives the refreshed local copy and re-renders.
 */
const PaypalDisputeModal = ({ dispute, onClose, onActionDone }) => {
  const [full, setFull] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("overview");
  const [busy, setBusy] = useState("");

  // Action-specific form state.
  const [msgText, setMsgText] = useState("");
  const [escalateNote, setEscalateNote] = useState("");
  const [acceptForm, setAcceptForm] = useState({ note: "", refund_value: "", accept_claim_type: "REFUND" });
  const [offerForm, setOfferForm] = useState({ offer_type: "REFUND", offer_value: "", note: "" });
  const [evidenceForm, setEvidenceForm] = useState({
    evidence_type: "PROOF_OF_FULFILLMENT",
    notes: "",
  });
  const fileInputRef = useRef(null);

  const isOpen = !!dispute;

  useEffect(() => {
    if (!isOpen) {
      setFull(null);
      setActiveTab("overview");
      setMsgText("");
      setEscalateNote("");
      setAcceptForm({ note: "", refund_value: "", accept_claim_type: "REFUND" });
      setOfferForm({ offer_type: "REFUND", offer_value: "", note: "" });
      setEvidenceForm({ evidence_type: "PROOF_OF_FULFILLMENT", notes: "" });
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }
    // Fetch the full local document on open.
    const load = async () => {
      setLoading(true);
      try {
        const r = await Utils._apiRequest({
          path: Enums.endpoints.paypal.DISPUTES_DETAIL,
          pathParams: { id: dispute.disputeId },
          type: "GET",
        });
        if (!r?.ok) throw new Error(r?.errors || "Không tải được dispute");
        setFull(r.item);
      } catch (err) {
        toast.error(err.message);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [isOpen, dispute?.disputeId]);

  const raw = full?.raw || {};
  const currency = full?.amount?.currency_code || raw?.dispute_amount?.currency_code || "USD";

  const messages = useMemo(() => {
    const arr = Array.isArray(raw?.messages) ? raw.messages : [];
    return [...arr].sort((a, b) => new Date(a.posted_by_time || a.time_posted || 0) - new Date(b.posted_by_time || b.time_posted || 0));
  }, [raw]);
  const evidences = Array.isArray(raw?.evidences) ? raw.evidences : [];
  const transactions = Array.isArray(raw?.disputed_transactions) ? raw.disputed_transactions : [];
  const offers = raw?.offer || raw?.offer_history || raw?.adjudications || [];

  const reloadAfterAction = (item) => {
    if (item) setFull((prev) => ({ ...(prev || {}), ...item }));
    if (typeof onActionDone === "function") onActionDone();
  };

  const callAction = async (key, path, payload) => {
    setBusy(key);
    try {
      const r = await Utils._apiRequest({
        path,
        pathParams: { id: dispute.disputeId },
        type: "POST",
        payload,
      });
      if (!r?.ok) throw new Error(r?.errors || "Action thất bại");
      reloadAfterAction(r.item);
      toast.success("Thao tác thành công");
      return r;
    } catch (err) {
      toast.error(err.message);
      throw err;
    } finally {
      setBusy("");
    }
  };

  const handleRefresh = async () => {
    setBusy("refresh");
    try {
      const r = await Utils._apiRequest({
        path: Enums.endpoints.paypal.DISPUTES_REFRESH,
        pathParams: { id: dispute.disputeId },
        type: "POST",
      });
      if (!r?.ok) throw new Error(r?.errors || "Refresh thất bại");
      reloadAfterAction(r.item);
      toast.success("Đã refresh từ PayPal");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy("");
    }
  };

  const handleSendMessage = async () => {
    if (!msgText.trim()) { toast.warning("Nhập nội dung trước"); return; }
    await callAction("message", Enums.endpoints.paypal.DISPUTES_SEND_MESSAGE, { message: msgText });
    setMsgText("");
  };

  const handleEscalate = async () => {
    if (!window.confirm("Escalate dispute này thành claim? Hành động không thể hoàn tác.")) return;
    await callAction("escalate", Enums.endpoints.paypal.DISPUTES_ESCALATE, { note: escalateNote });
    setEscalateNote("");
  };

  const handleAccept = async () => {
    if (!acceptForm.note.trim()) { toast.warning("Nhập note trước"); return; }
    if (!window.confirm("Accept claim của buyer? Hành động không thể hoàn tác.")) return;
    const payload = { note: acceptForm.note, accept_claim_type: acceptForm.accept_claim_type };
    if (acceptForm.refund_value) {
      payload.refund_amount = { currency_code: currency, value: String(acceptForm.refund_value) };
    }
    await callAction("accept", Enums.endpoints.paypal.DISPUTES_ACCEPT_CLAIM, payload);
  };

  const handleMakeOffer = async () => {
    if (!offerForm.note.trim()) { toast.warning("Nhập note trước"); return; }
    const payload = { offer_type: offerForm.offer_type, note: offerForm.note };
    if (offerForm.offer_value) {
      payload.offer_amount = { currency_code: currency, value: String(offerForm.offer_value) };
    }
    await callAction("offer", Enums.endpoints.paypal.DISPUTES_OFFER, payload);
  };

  const handleProvideEvidence = async () => {
    const files = fileInputRef.current?.files;
    if (!files || files.length === 0) { toast.warning("Chọn file trước"); return; }
    setBusy("evidence");
    try {
      const fd = new FormData();
      const documents = [];
      for (let i = 0; i < files.length; i++) {
        fd.append("file", files[i]);
        documents.push({ name: files[i].name });
      }
      const inputPayload = {
        evidences: [{
          evidence_type: evidenceForm.evidence_type,
          notes: evidenceForm.notes || "",
          documents,
        }],
      };
      fd.append("input", JSON.stringify(inputPayload));

      const apiURI = `${Enums.endpoints.paypal.DISPUTES_PROVIDE_EVIDENCE}`.replace("[id]", dispute.disputeId);
      const r = await Utils._apiRequest({
        path: apiURI,
        type: "POST",
        payload: fd,
        dataType: "multipart/form-data",
      });
      if (!r?.ok) throw new Error(r?.errors || "Provide evidence thất bại");
      reloadAfterAction(r.item);
      toast.success("Đã upload evidence");
      if (fileInputRef.current) fileInputRef.current.value = "";
      setEvidenceForm({ ...evidenceForm, notes: "" });
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy("");
    }
  };

  if (!isOpen) return null;

  const stage = full?.disputeLifeCycleStage || raw?.dispute_life_cycle_stage;
  const status = full?.status || raw?.status;
  const state = full?.disputeState || raw?.dispute_state;
  const outcomeCode = full?.outcome?.outcome_code || raw?.dispute_outcome?.outcome_code || "";

  const isResolved = (status || "").toUpperCase() === "RESOLVED";
  const isInquiry = (stage || "").toUpperCase() === "INQUIRY";

  return (
    <Modal show={isOpen} onHide={onClose} size="xl" backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>
          <FontAwesomeIcon icon={faGavel} className="me-2" />
          Dispute <code>{dispute.disputeId}</code>
          {full?.account?.accountName && (
            <span className="ms-2 text-muted small">— {full.account.accountName}</span>
          )}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ maxHeight: "75vh", overflowY: "auto" }}>
        {loading && <div className="text-center py-4"><Spinner /></div>}
        {!loading && !full && <Alert variant="warning">Không tải được dữ liệu dispute.</Alert>}
        {!loading && full && (
          <>
            {/* Top summary */}
            <div className="mb-3 d-flex flex-wrap gap-2 align-items-center">
              <Badge bg="primary">{state || status || "—"}</Badge>
              <Badge bg="info">{stage || "—"}</Badge>
              <Badge bg={outcomeCode === "RESOLVED_SELLER_FAVOUR" ? "success" : outcomeCode === "RESOLVED_BUYER_FAVOUR" ? "danger" : "secondary"}>
                Outcome: {outcomeCode || "NONE"}
              </Badge>
              <Badge bg="dark">
                Amount: {full.amount?.value} {full.amount?.currency_code}
              </Badge>
              {full.outcome?.amount_refunded?.value && (
                <Badge bg="warning">
                  Refunded: {full.outcome.amount_refunded.value} {full.outcome.amount_refunded.currency_code}
                </Badge>
              )}
              <span className="ms-auto small text-muted">
                <FontAwesomeIcon icon={faClock} className="me-1" />
                Sync: {full.syncedAt ? new Date(full.syncedAt).toLocaleString() : "—"}
              </span>
              <Button size="sm" variant="outline-secondary" onClick={handleRefresh} disabled={!!busy}>
                {busy === "refresh" ? <Spinner size="sm" /> : <><FontAwesomeIcon icon={faRotate} /> Refresh</>}
              </Button>
            </div>

            <Tab.Container activeKey={activeTab} onSelect={(k) => setActiveTab(k || "overview")}>
              <Nav variant="tabs" className="mb-3">
                <Nav.Item><Nav.Link eventKey="overview">Tổng quan</Nav.Link></Nav.Item>
                <Nav.Item><Nav.Link eventKey="messages">Messages ({messages.length})</Nav.Link></Nav.Item>
                <Nav.Item><Nav.Link eventKey="evidences">Evidences ({evidences.length})</Nav.Link></Nav.Item>
                <Nav.Item><Nav.Link eventKey="actions">Actions</Nav.Link></Nav.Item>
                <Nav.Item><Nav.Link eventKey="raw">Raw JSON</Nav.Link></Nav.Item>
              </Nav>

              <Tab.Content>
                {/* ----- Overview ----- */}
                <Tab.Pane eventKey="overview">
                  <Table size="sm" striped borderless>
                    <tbody>
                      <tr><th style={{ width: 200 }}>Reason</th><td>{full.reason || raw.reason || "—"}</td></tr>
                      <tr><th>Channel / Category</th><td>{full.disputeChannel || "—"} / {full.disputeCategory || "—"}</td></tr>
                      <tr><th>Created</th><td>{full.createTime ? new Date(full.createTime).toLocaleString() : "—"}</td></tr>
                      <tr><th>Updated</th><td>{full.updateTime ? new Date(full.updateTime).toLocaleString() : "—"}</td></tr>
                      <tr><th>Seller response due</th><td>{full.sellerResponseDueDate ? new Date(full.sellerResponseDueDate).toLocaleString() : "—"}</td></tr>
                      <tr><th>Buyer response due</th><td>{full.buyerResponseDueDate ? new Date(full.buyerResponseDueDate).toLocaleString() : "—"}</td></tr>
                      <tr><th>Buyer</th><td>{full.buyer?.email || "—"} {full.buyer?.name && `(${full.buyer.name})`}</td></tr>
                      <tr><th>Seller</th><td>{full.seller?.email || "—"} {full.seller?.merchant_id && `(${full.seller.merchant_id})`}</td></tr>
                    </tbody>
                  </Table>

                  {transactions.length > 0 && (
                    <>
                      <h6 className="mt-3">Transactions</h6>
                      <Table size="sm" bordered>
                        <thead className="table-light"><tr>
                          <th>Seller TX</th><th>Buyer TX</th><th>Invoice</th><th>Amount</th><th>Created</th>
                        </tr></thead>
                        <tbody>
                          {transactions.map((t, i) => (
                            <tr key={t.seller_transaction_id || t.buyer_transaction_id || i}>
                              <td><code className="small">{t.seller_transaction_id || "—"}</code></td>
                              <td><code className="small">{t.buyer_transaction_id || "—"}</code></td>
                              <td>{t.invoice_number || "—"}</td>
                              <td>{t.gross_amount ? `${t.gross_amount.value} ${t.gross_amount.currency_code}` : "—"}</td>
                              <td className="small">{t.create_time ? new Date(t.create_time).toLocaleString() : "—"}</td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </>
                  )}

                  {Array.isArray(offers) && offers.length > 0 && (
                    <>
                      <h6 className="mt-3">Offers / Adjudications</h6>
                      <pre className="small bg-light p-2 rounded" style={{ maxHeight: 200, overflow: "auto" }}>
                        {JSON.stringify(offers, null, 2)}
                      </pre>
                    </>
                  )}
                </Tab.Pane>

                {/* ----- Messages ----- */}
                <Tab.Pane eventKey="messages">
                  {messages.length === 0 && <div className="text-muted">Chưa có message nào.</div>}
                  {messages.map((m, i) => (
                    <div key={i} className={`p-2 mb-2 rounded ${m.posted_by === "SELLER" ? "bg-primary-subtle" : "bg-light"}`}>
                      <div className="small">
                        <strong>{m.posted_by || "—"}</strong>
                        <span className="text-muted ms-2">{m.posted_by_time ? new Date(m.posted_by_time).toLocaleString() : ""}</span>
                      </div>
                      <div style={{ whiteSpace: "pre-wrap" }}>{m.content}</div>
                      {Array.isArray(m.documents) && m.documents.map((d, j) => (
                        <div key={j} className="small">
                          <a href={d.url || d.document_url} target="_blank" rel="noreferrer">{d.name || `attachment ${j + 1}`}</a>
                        </div>
                      ))}
                    </div>
                  ))}
                </Tab.Pane>

                {/* ----- Evidences ----- */}
                <Tab.Pane eventKey="evidences">
                  {evidences.length === 0 && <div className="text-muted">Chưa có evidence.</div>}
                  {evidences.map((e, i) => (
                    <div key={i} className="border rounded p-2 mb-2">
                      <div><strong>{e.evidence_type || "—"}</strong> <span className="text-muted small">— {e.source || "—"}</span></div>
                      {e.notes && <div className="small mt-1" style={{ whiteSpace: "pre-wrap" }}>{e.notes}</div>}
                      {Array.isArray(e.documents) && e.documents.map((d, j) => (
                        <div key={j} className="small">
                          <a href={d.url || d.document_url} target="_blank" rel="noreferrer">{d.name || `document ${j + 1}`}</a>
                        </div>
                      ))}
                    </div>
                  ))}
                </Tab.Pane>

                {/* ----- Actions ----- */}
                <Tab.Pane eventKey="actions">
                  {isResolved && (
                    <Alert variant="info" className="py-2">
                      Dispute đã <strong>RESOLVED</strong> — các action có thể không khả dụng.
                    </Alert>
                  )}

                  {/* Send message */}
                  <div className="border rounded p-3 mb-3">
                    <h6><FontAwesomeIcon icon={faPaperPlane} className="me-2" /> Send Message</h6>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      value={msgText}
                      onChange={(e) => setMsgText(e.target.value)}
                      placeholder="Nội dung gửi cho buyer / PayPal…"
                      maxLength={2000}
                    />
                    <div className="d-flex justify-content-end mt-2">
                      <Button size="sm" variant="primary" onClick={handleSendMessage} disabled={!!busy}>
                        {busy === "message" ? <Spinner size="sm" /> : "Gửi message"}
                      </Button>
                    </div>
                  </div>

                  {/* Provide evidence */}
                  <div className="border rounded p-3 mb-3">
                    <h6><FontAwesomeIcon icon={faFileUpload} className="me-2" /> Provide Evidence</h6>
                    <Form.Group className="mb-2">
                      <Form.Label className="small">Loại evidence</Form.Label>
                      <Form.Select
                        size="sm"
                        value={evidenceForm.evidence_type}
                        onChange={(e) => setEvidenceForm({ ...evidenceForm, evidence_type: e.target.value })}
                      >
                        <option value="PROOF_OF_FULFILLMENT">PROOF_OF_FULFILLMENT</option>
                        <option value="PROOF_OF_REFUND">PROOF_OF_REFUND</option>
                        <option value="PROOF_OF_DELIVERY">PROOF_OF_DELIVERY</option>
                        <option value="PROOF_OF_RETURN_DELIVERY">PROOF_OF_RETURN_DELIVERY</option>
                        <option value="RECEIPT">RECEIPT</option>
                        <option value="ORDER_DETAILS">ORDER_DETAILS</option>
                        <option value="TRACKING_INFO">TRACKING_INFO</option>
                        <option value="OTHER">OTHER</option>
                      </Form.Select>
                    </Form.Group>
                    <Form.Group className="mb-2">
                      <Form.Label className="small">Files (PDF/JPG/PNG, ≤ 10MB/file, ≤ 10 files)</Form.Label>
                      <Form.Control type="file" size="sm" multiple ref={fileInputRef} />
                    </Form.Group>
                    <Form.Group className="mb-2">
                      <Form.Label className="small">Notes</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={2}
                        size="sm"
                        value={evidenceForm.notes}
                        onChange={(e) => setEvidenceForm({ ...evidenceForm, notes: e.target.value })}
                      />
                    </Form.Group>
                    <div className="d-flex justify-content-end">
                      <Button size="sm" variant="primary" onClick={handleProvideEvidence} disabled={!!busy}>
                        {busy === "evidence" ? <Spinner size="sm" /> : "Upload evidence"}
                      </Button>
                    </div>
                  </div>

                  {/* Make offer */}
                  <div className="border rounded p-3 mb-3">
                    <h6><FontAwesomeIcon icon={faHandshake} className="me-2" /> Offer Resolution</h6>
                    <div className="row g-2">
                      <div className="col-md-4">
                        <Form.Label className="small">Offer type</Form.Label>
                        <Form.Select
                          size="sm"
                          value={offerForm.offer_type}
                          onChange={(e) => setOfferForm({ ...offerForm, offer_type: e.target.value })}
                        >
                          <option value="REFUND">REFUND (one-time)</option>
                          <option value="REFUND_WITH_RETURN">REFUND_WITH_RETURN</option>
                          <option value="REPLACEMENT">REPLACEMENT</option>
                          <option value="REFUND_WITH_REPLACEMENT">REFUND_WITH_REPLACEMENT</option>
                        </Form.Select>
                      </div>
                      <div className="col-md-4">
                        <Form.Label className="small">Amount ({currency})</Form.Label>
                        <Form.Control
                          type="number"
                          size="sm"
                          step="0.01"
                          value={offerForm.offer_value}
                          onChange={(e) => setOfferForm({ ...offerForm, offer_value: e.target.value })}
                          placeholder="vd: 12.50"
                        />
                      </div>
                    </div>
                    <Form.Group className="mt-2">
                      <Form.Label className="small">Note (gửi cho buyer)</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={2}
                        size="sm"
                        value={offerForm.note}
                        onChange={(e) => setOfferForm({ ...offerForm, note: e.target.value })}
                      />
                    </Form.Group>
                    <div className="d-flex justify-content-end mt-2">
                      <Button size="sm" variant="warning" onClick={handleMakeOffer} disabled={!!busy}>
                        {busy === "offer" ? <Spinner size="sm" /> : "Gửi offer"}
                      </Button>
                    </div>
                  </div>

                  {/* Accept claim */}
                  <div className="border rounded p-3 mb-3">
                    <h6 className="text-danger">
                      <FontAwesomeIcon icon={faMoneyBillTransfer} className="me-2" /> Accept Claim (refund 100%)
                    </h6>
                    <p className="small text-muted mb-2">
                      Chấp nhận yêu cầu của buyer — PayPal sẽ refund toàn bộ (hoặc số tiền chỉ định) và đóng dispute.
                    </p>
                    <div className="row g-2">
                      <div className="col-md-4">
                        <Form.Label className="small">Accept type</Form.Label>
                        <Form.Select
                          size="sm"
                          value={acceptForm.accept_claim_type}
                          onChange={(e) => setAcceptForm({ ...acceptForm, accept_claim_type: e.target.value })}
                        >
                          <option value="REFUND">REFUND</option>
                          <option value="REFUND_WITH_RETURN">REFUND_WITH_RETURN</option>
                          <option value="REFUND_WITH_REPLACEMENT">REFUND_WITH_REPLACEMENT</option>
                        </Form.Select>
                      </div>
                      <div className="col-md-4">
                        <Form.Label className="small">Refund amount (tuỳ chọn)</Form.Label>
                        <Form.Control
                          type="number"
                          size="sm"
                          step="0.01"
                          value={acceptForm.refund_value}
                          onChange={(e) => setAcceptForm({ ...acceptForm, refund_value: e.target.value })}
                          placeholder="Để trống = full"
                        />
                      </div>
                    </div>
                    <Form.Group className="mt-2">
                      <Form.Label className="small">Note</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={2}
                        size="sm"
                        value={acceptForm.note}
                        onChange={(e) => setAcceptForm({ ...acceptForm, note: e.target.value })}
                      />
                    </Form.Group>
                    <div className="d-flex justify-content-end mt-2">
                      <Button size="sm" variant="danger" onClick={handleAccept} disabled={!!busy}>
                        {busy === "accept" ? <Spinner size="sm" /> : "Accept Claim"}
                      </Button>
                    </div>
                  </div>

                  {/* Escalate */}
                  {isInquiry && (
                    <div className="border rounded p-3 mb-3">
                      <h6 className="text-dark">
                        <FontAwesomeIcon icon={faArrowUp} className="me-2" /> Escalate to Claim
                      </h6>
                      <p className="small text-muted mb-2">
                        Chuyển INQUIRY → CHARGEBACK / CLAIM cho PayPal phán quyết.
                      </p>
                      <Form.Group>
                        <Form.Label className="small">Note</Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={2}
                          size="sm"
                          value={escalateNote}
                          onChange={(e) => setEscalateNote(e.target.value)}
                        />
                      </Form.Group>
                      <div className="d-flex justify-content-end mt-2">
                        <Button size="sm" variant="dark" onClick={handleEscalate} disabled={!!busy}>
                          {busy === "escalate" ? <Spinner size="sm" /> : "Escalate"}
                        </Button>
                      </div>
                    </div>
                  )}
                </Tab.Pane>

                {/* ----- Raw JSON ----- */}
                <Tab.Pane eventKey="raw">
                  <pre className="small bg-light p-2 rounded" style={{ maxHeight: 500, overflow: "auto" }}>
                    {JSON.stringify(raw, null, 2)}
                  </pre>
                </Tab.Pane>
              </Tab.Content>
            </Tab.Container>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>Đóng</Button>
      </Modal.Footer>
    </Modal>
  );
};

export default PaypalDisputeModal;
